import type { SuggestionItem } from "@digdir/designsystemet-react";
import type { ReportCatalogEntry, ReportDetailField } from "../data/types";
import { lastChangedFieldLabel, type SortDirection, type SortOption } from "./filterDefinitions";

function normalize(value: string): string {
    return value.toLocaleLowerCase("nb").trim();
}

export function createSuggestionOptions(values: string[]): SuggestionItem[] {
    const uniqueByNormalized = new Map<string, string>();

    values
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
        .forEach((value) => {
            const normalized = normalize(value);
            if (!uniqueByNormalized.has(normalized)) {
                uniqueByNormalized.set(normalized, value);
            }
        });

    return Array.from(uniqueByNormalized.values())
        .sort((left, right) => left.localeCompare(right, "nb", { sensitivity: "base", numeric: true }))
        .map((value) => ({ label: value, value }));
}

export function parseLastChangedValue(value: string): number {
    const trimmedValue = value.trim();
    const datePartsMatch = trimmedValue.match(/^(\d{1,4})[./-](\d{1,2})[./-](\d{1,4})$/);
    const monthYearMatch = trimmedValue.match(/^([A-Za-zÆØÅæøå]{3,})-(\d{2,4})$/);

    const normalizeYear = (yearValue: number) => {
        if (yearValue >= 100) {
            return yearValue;
        }

        return yearValue <= 69 ? 2000 + yearValue : 1900 + yearValue;
    };

    const toTimestampIfValid = (year: number, month: number, day: number) => {
        if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
            return Number.NEGATIVE_INFINITY;
        }

        if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) {
            return Number.NEGATIVE_INFINITY;
        }

        const date = new Date(year, month - 1, day);
        const isValidDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

        return isValidDate ? date.getTime() : Number.NEGATIVE_INFINITY;
    };

    const monthByName: Record<string, number> = {
        jan: 1,
        januar: 1,
        feb: 2,
        februar: 2,
        mar: 3,
        mars: 3,
        apr: 4,
        april: 4,
        may: 5,
        mai: 5,
        jun: 6,
        juni: 6,
        jul: 7,
        juli: 7,
        aug: 8,
        august: 8,
        sep: 9,
        sept: 9,
        september: 9,
        oct: 10,
        okt: 10,
        oktober: 10,
        nov: 11,
        november: 11,
        dec: 12,
        des: 12,
        desember: 12,
    };

    if (monthYearMatch) {
        const monthName = monthYearMatch[1].toLocaleLowerCase("nb");
        const month = monthByName[monthName];
        const year = normalizeYear(Number(monthYearMatch[2]));

        if (!month) {
            return Number.NEGATIVE_INFINITY;
        }

        return toTimestampIfValid(year, month, 1);
    }

    let day: number;
    let month: number;
    let year: number;

    if (datePartsMatch) {
        const first = Number(datePartsMatch[1]);
        const second = Number(datePartsMatch[2]);
        const third = Number(datePartsMatch[3]);

        if (datePartsMatch[1].length === 4) {
            year = first;
            month = second;
            day = third;
        } else {
            year = normalizeYear(third);

            if (first > 12 && second <= 12) {
                // Day-first format (e.g. 25/11/2025)
                day = first;
                month = second;
            } else if (second > 12 && first <= 12) {
                // Month-first format (e.g. 4/23/26)
                month = first;
                day = second;
            } else {
                // Ambiguous values default to day-first to preserve existing behavior.
                day = first;
                month = second;
            }
        }
    } else {
        return Number.NEGATIVE_INFINITY;
    }

    return toTimestampIfValid(year, month, day);
}

export function isLastChangedField(field: ReportDetailField): boolean {
    return field.label.trim().toLowerCase() === lastChangedFieldLabel;
}

function getLastChangedFieldValue(row: ReportCatalogEntry): string {
    const lastChangedField = row.detailFields.find((field) => isLastChangedField(field));
    return lastChangedField?.value ?? "";
}

function compareText(left: string, right: string): number {
    return left.localeCompare(right, "nb", {
        sensitivity: "base",
        numeric: true,
    });
}

export function sortRows(
    rows: ReportCatalogEntry[],
    sortOption: SortOption,
    sortDirection: SortDirection,
): ReportCatalogEntry[] {
    const sorted = [...rows];

    sorted.sort((left, right) => {
        let result: number;

        if (sortOption === "lastChanged") {
            const leftTimestamp = parseLastChangedValue(getLastChangedFieldValue(left));
            const rightTimestamp = parseLastChangedValue(getLastChangedFieldValue(right));
            result = leftTimestamp === rightTimestamp ? 0 : leftTimestamp < rightTimestamp ? -1 : 1;
        } else {
            const leftValue =
                sortOption === "title"
                    ? left.title
                    : sortOption === "area"
                      ? left.area
                      : sortOption === "owner"
                        ? left.owner
                        : left.targetGroups;
            const rightValue =
                sortOption === "title"
                    ? right.title
                    : sortOption === "area"
                      ? right.area
                      : sortOption === "owner"
                        ? right.owner
                        : right.targetGroups;

            result = compareText(leftValue, rightValue);
        }

        if (result !== 0) {
            return sortDirection === "asc" ? result : -result;
        }

        return compareText(left.title, right.title);
    });

    return sorted;
}

export function filterRowsBySuggestions(
    rows: ReportCatalogEntry[],
    selectedAreaValues: Set<string>,
    selectedOwnerValues: Set<string>,
    selectedTargetGroupValues: Set<string>,
): ReportCatalogEntry[] {
    return rows.filter((row) => {
        const areaMatches = selectedAreaValues.size === 0 || selectedAreaValues.has(row.area);
        const ownerMatches = selectedOwnerValues.size === 0 || selectedOwnerValues.has(row.owner);
        const targetGroupMatches =
            selectedTargetGroupValues.size === 0 || selectedTargetGroupValues.has(row.targetGroups);

        return areaMatches && ownerMatches && targetGroupMatches;
    });
}
